import { setTextToClipboard } from './common/clipboard'
import { asyncRun, cancelExecution, setInputEventListeners, startWorker } from './worker-api'
import { getCode, initializeMonacoEditor, setFocusToMonacoEditor } from './common/monaco-editor'

import './programming.css'

const codeEditorWrapperId = 'tab-programming-ide-container'
const codeEditorId = 'code-editor'
const outputId = 'code-output'
const errorId = 'code-error'
const userInputId = 'python-input'
const userInputContainerId = 'user-input-container'
const executeButtonId = 'execute-code'
const copyCodeToClipboardButtonId = 'copy-code'
const copyOutputToClipboardButtonId = 'copy-output'
const cancelButtonsId = 'cancel'
const inputButtonId = 'python-input-submit'
const isSingleColumn = () => window.innerWidth / 2 <= document.getElementById(codeEditorWrapperId).offsetWidth

const getEditorBottom = () => {
  const rect = document.getElementById(codeEditorWrapperId).getBoundingClientRect()
  return rect.top + rect.height
}

const getSuggestedOutputAndErrorHeight = () => {
  const singleColumnReservedSpaceBottom = 200
  const twoColumnReservedSpaceBottom = 50

  const bottomMargin = isSingleColumn() ? singleColumnReservedSpaceBottom : twoColumnReservedSpaceBottom

  return window.innerHeight - getEditorBottom() - bottomMargin
}

const calculateHeight = (element: HTMLElement) => {
  const suggestedHeight = getSuggestedOutputAndErrorHeight()
  const contentHeight = element.scrollHeight
  return contentHeight > suggestedHeight ? suggestedHeight : contentHeight + 20
}

const setOutputAndErrorHeight = (elementId: string) => {
  const element = document.getElementById(elementId)
  const setHeight = (newHeight: number) => {
    if (element) {
      element.style.height = `{newHeight}px`
    }
  }

  setHeight(0)
  setHeight(calculateHeight(element))
}

const showErrorArea = () => {
  const errorElement = document.getElementById(errorId)
  if (errorElement) {
    errorElement.style.display = 'block'
  }
  setOutputAndErrorHeight(errorId)
}

const hideBothAreas = () => {
  const outputElement = document.getElementById(outputId)
  const errorElement = document.getElementById(errorId)
  const inputElement = document.getElementById(userInputContainerId)
  if (outputElement) {
    outputElement.style.display = 'none'
  }
  if (inputElement) {
    inputElement.style.display = 'none'
  }
  if (errorElement) {
    errorElement.style.display = 'none'
  }
}

const showOutputArea = () => {
  const outputElement = document.getElementById(outputId)
  const errorElement = document.getElementById(errorId)
  if (outputElement) {
    outputElement.style.display = 'block'
  }
  setOutputAndErrorHeight(outputId)
}

const printStderr = (text: string) => {
  document.getElementById(errorId).innerHTML = text
  showErrorArea()
}

const clearStdout = () => {
  document.getElementById(outputId).innerHTML = ''
}

const clearStderr = () => {
  document.getElementById(errorId).innerHTML = ''
}

const clearInput = () => {
  document.getElementById(userInputId).innerHTML = ''
}

const printStdout = (text: string) => {
  showOutputArea()
  const outputElement = document.getElementById(outputId)
  if (outputElement) {
    outputElement.textContent += `${text}\n`
  }
}

let executingCode: boolean = false

const executeCode = async () => {
  if (executingCode) {
    return
  }
  executingCode = true
  const executeButton = document.getElementById(executeButtonId)
  const cancelButton = document.getElementById(cancelButtonsId)
  executeButton?.classList.add('hidden')
  cancelButton?.classList.remove('hidden')

  hideBothAreas()
  clearStdout()
  clearStderr()
  clearInput()
  const code = getCode()

  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    await asyncRun(code, printStdout, printStderr)
  } catch (err: any) {
    if (err instanceof Error) {
      printStderr(err.toString())
    }
  } finally {
    executingCode = false
    executeButton?.classList.remove('hidden')
    cancelButton?.classList.add('hidden')
  }
}

const cancelCodeExecution = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  cancelExecution()
}

const setButtonActive = (button: HTMLElement) => {
  button.classList.add('active')
  setTimeout(() => {
    button.classList.remove('active')
  }, 1500)
}

const processAccessibilityKeybindings = async (event: KeyboardEvent) => {
  if (event.code == 'KeyE' && event.altKey) {
    event.preventDefault()
    setFocusToMonacoEditor()
    return
  }

  if (event.code == 'KeyX' && event.altKey) {
    event.preventDefault()
    try {
      if (executingCode) {
        cancelCodeExecution()
      } else {
        await executeCode()
      }
    } catch (e) {
      console.log(e)
    }
    return
  }

  if (event.code === 'KeyI' && event.altKey) {
    event.preventDefault()
    copyCodeToClipboard()
    return
  }

  if (event.code === 'KeyO' && event.altKey) {
    event.preventDefault()
    copyCodeOutputToClipboard()
    return
  }

  if (event.code === 'KeyQ' && event.altKey) {
    event.preventDefault()
    cancelCodeExecution()
    return
  }

  if (event.code === 'KeyP' && event.altKey) {
    event.preventDefault()
    const userInput = document.getElementById(userInputId)
    if (userInput) {
      userInput.focus()
    }
    return
  }
}

const accessibilityKeydownHandler = (event: KeyboardEvent) => {
  void processAccessibilityKeybindings(event)
}

const copyCodeToClipboard = () => {
  const text = getCode()
  setTextToClipboard(text, 'copying_box_input')
  const copyCodeButton = document.getElementById(copyCodeToClipboardButtonId)
  setButtonActive(copyCodeButton)
}

const copyCodeOutputToClipboard = () => {
  const text = document.getElementById(outputId).textContent ?? ''
  setTextToClipboard(text, 'copying_box_output')
  const copyCodeOutputButton = document.getElementById(copyOutputToClipboardButtonId)
  setButtonActive(copyCodeOutputButton)
}

export const initializeProgrammingView = async () => {
  await initializeMonacoEditor(codeEditorId)
  hideBothAreas()
  showOutputArea()
  startWorker()

  const executeButton = document.getElementById(executeButtonId)
  executeButton.addEventListener('click', () => {
    void executeCode()
  })

  const copyCodeToClipboardButton = document.getElementById(copyCodeToClipboardButtonId)
  copyCodeToClipboardButton.addEventListener('click', copyCodeToClipboard)

  const copyCodeOutputToClipboardButton = document.getElementById(copyOutputToClipboardButtonId)
  copyCodeOutputToClipboardButton.addEventListener('click', copyCodeOutputToClipboard)

  const cancelButton = document.getElementById(cancelButtonsId)
  cancelButton.addEventListener('click', cancelCodeExecution)

  const inputButton = document.getElementById(inputButtonId)
  inputButton.addEventListener('click', setInputEventListeners)

  document.addEventListener('keydown', accessibilityKeydownHandler)
}

export const teardownProgrammingTab = () => {
  document.removeEventListener('keydown', accessibilityKeydownHandler)
}
